/* eslint-disable */
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Slider,
  Checkbox,
  FormControlLabel,
  Button,
  Chip,
  Paper,
  Grid,
  Divider,
  Card,
  CardContent,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import {
  FilterList as FilterIcon,
  Clear as ClearIcon,
  Home as HomeIcon,
  Business as BusinessIcon,
  Landscape as LandscapeIcon,
  Factory as FactoryIcon,
  Bed as BedIcon,
  Bathtub as BathIcon,
} from "@mui/icons-material";
import { useState, useEffect } from "react";
import { masterDataAPI } from "../services/api";

const MIN_PRICE = 1000000;
const MAX_PRICE = 500000000;

const propertySubTypes = {
  Residential: ["Flat", "Villa", "Plot"],
  Commercial: ["Complex", "Shop", "Plot"],
  Agricultural: ["Land", "Plot"],
};

const formatPriceRangeLabel = (value) => {
  if (value >= 10000000) {
    const crores = value / 10000000;
    return `₹${Number.isInteger(crores) ? crores : crores.toFixed(1)}Cr`;
  }

  return `₹${(value / 100000).toFixed(1)}L`;
};

export function PropertyFilters({
  filters,
  onFiltersChange,
  activeFiltersCount,
  showHeader = true,
}) {
  const allowedDistrictValues = ["Udupi", "Dakshina Kannada"];
  const [districts, setDistricts] = useState([]);
  const [districtsWithTaluks, setDistrictsWithTaluks] = useState([]);
  const [taluks, setTaluks] = useState([]);
  const [loadingDistricts, setLoadingDistricts] = useState(false);

  // Fetch districts with taluks on component mount
  useEffect(() => {
    const fetchDistricts = async () => {
      setLoadingDistricts(true);
      try {
        const response = await masterDataAPI.getAllMasterData();
        const allDistricts = response.data.data.districtsWithTaluks || [];
        setDistrictsWithTaluks(allDistricts);
        setDistricts(allDistricts.map(d => ({ value: d.value, label: d.label })));
      } catch (error) {
        setDistricts([]);
        setDistrictsWithTaluks([]);
      } finally {
        setLoadingDistricts(false);
      }
    };
    fetchDistricts();
  }, []);

  // Update taluks when district changes
  useEffect(() => {
    if (!filters.district || filters.district === "") {
      setTaluks([]);
      return;
    }

    const selectedDistrict = districtsWithTaluks.find(d => d.value === filters.district);
    if (selectedDistrict) {
      setTaluks(selectedDistrict.taluks.map(t => ({ value: t, label: t })));
    } else {
      setTaluks([]);
    }
  }, [filters.district, districtsWithTaluks]);

  const updateFilter = (key, value) => {
    // When district changes, clear taluk selection
    if (key === "district") {
      onFiltersChange("taluk", "");
      setTaluks([]);
    }
    // Keep type/subtype combinations valid.
    if (key === "propertyType") {
      onFiltersChange("propertySubType", "");
    }
    onFiltersChange(key, value);
  };

  const toggleAmenity = (amenity) => {
    const newAmenities = filters.amenities.includes(amenity)
      ? filters.amenities.filter((a) => a !== amenity)
      : [...filters.amenities, amenity];
    updateFilter("amenities", newAmenities);
  };

  const clearFilters = () => {
    updateFilter("priceRange", [MIN_PRICE, MAX_PRICE]);
    updateFilter("propertyType", "");
    updateFilter("propertySubType", "");
    updateFilter("district", "");
    updateFilter("taluk", "");
    updateFilter("bedrooms", "");
    updateFilter("bathrooms", "");
    updateFilter("amenities", []);
    updateFilter("sortBy", "newest");
  };

  const countActiveFilters = () => {
    let count = 0;
    if (filters.propertyType && filters.propertyType !== "all") count++;
    if (filters.propertySubType && filters.propertySubType !== "all") count++;
    if (filters.district && filters.district !== "all") count++;
    if (filters.taluk && filters.taluk !== "all") count++;
    if (filters.bedrooms && filters.bedrooms !== "any") count++;
    if (filters.bathrooms && filters.bathrooms !== "any") count++;
    if (filters.priceRange[0] > MIN_PRICE || filters.priceRange[1] < MAX_PRICE) count++;
    if (filters.amenities.length > 0) count++;
    return count;
  };

  const activeCount = countActiveFilters();
  const visibleDistricts = districts.filter((district) =>
    allowedDistrictValues.includes(district.value)
  );
  const availableSubTypes = propertySubTypes[filters.propertyType] || [];

  const FilterContent = () => (
    <Box sx={{ space: 3 }}>
      {/* Price Range */}
      <Card variant="outlined" sx={{ mb: 3, borderRadius: 2 }}>
        <CardContent>
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 600,
              mb: 3,
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            💰 Price Range
          </Typography>
          <Box sx={{ px: 2 }}>
            <Slider
              value={filters.priceRange}
              onChange={(_, value) => updateFilter("priceRange", value)}
              max={MAX_PRICE}
              min={MIN_PRICE}
              step={500000}
              valueLabelDisplay="auto"
              valueLabelFormat={formatPriceRangeLabel}
              sx={{ mb: 2 }}
            />
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Chip
                label={formatPriceRangeLabel(filters.priceRange[0])}
                size="small"
                color="primary"
                variant="outlined"
              />
              <Chip
                label={formatPriceRangeLabel(filters.priceRange[1])}
                size="small"
                color="primary"
                variant="outlined"
              />
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Property Details */}
      <Card variant="outlined" sx={{ mb: 3, borderRadius: 2 }}>
        <CardContent>
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 600,
              mb: 2,
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            🏠 Property Details
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Property Type</InputLabel>
                <Select
                  value={filters.propertyType || ""}
                  label="Property Type"
                  onChange={(e) => updateFilter("propertyType", e.target.value)}
                >
                  <MenuItem value="">All types</MenuItem>
                  <MenuItem value="Residential">Residential</MenuItem>
                  <MenuItem value="Commercial">Commercial</MenuItem>
                  <MenuItem value="Agricultural">Agricultural</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small" disabled={!filters.propertyType}>
                <InputLabel>Property Sub Type</InputLabel>
                <Select
                  value={filters.propertySubType || ""}
                  label="Property Sub Type"
                  onChange={(e) =>
                    updateFilter("propertySubType", e.target.value)
                  }
                >
                  <MenuItem value="">All sub types</MenuItem>
                  {availableSubTypes.map((subType) => (
                    <MenuItem key={subType} value={subType}>
                      {subType}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Location */}
      <Card variant="outlined" sx={{ mb: 3, borderRadius: 2 }}>
        <CardContent>
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 600,
              mb: 2,
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            📍 Location
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>District</InputLabel>
                <Select
                  value={filters.district || ""}
                  label="District"
                  onChange={(e) => updateFilter("district", e.target.value)}
                  disabled={loadingDistricts}
                >
                  <MenuItem value="">All districts</MenuItem>
                  {visibleDistricts.map((district) => (
                    <MenuItem key={district.value} value={district.value}>
                      {district.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Taluk</InputLabel>
                <Select
                  value={filters.taluk || ""}
                  label="Taluk"
                  onChange={(e) => updateFilter("taluk", e.target.value)}
                  disabled={!filters.district}
                >
                  <MenuItem value="">All taluks</MenuItem>
                  {taluks.map((taluk) => (
                    <MenuItem key={taluk.value} value={taluk.value}>
                      {taluk.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Rooms */}
      <Card variant="outlined" sx={{ mb: 3, borderRadius: 2 }}>
        <CardContent>
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 600,
              mb: 2,
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            🛏️ Rooms
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                <BedIcon
                  sx={{ fontSize: 16, mr: 0.5, verticalAlign: "middle" }}
                />
                Bedrooms
              </Typography>
              <ToggleButtonGroup
                value={filters.bedrooms || ""}
                exclusive
                onChange={(e, value) =>
                  value !== null && updateFilter("bedrooms", value)
                }
                size="small"
                fullWidth
              >
                <ToggleButton value="">Any</ToggleButton>
                <ToggleButton value="1">1</ToggleButton>
                <ToggleButton value="2">2</ToggleButton>
                <ToggleButton value="3">3</ToggleButton>
                <ToggleButton value="4">4+</ToggleButton>
              </ToggleButtonGroup>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                <BathIcon
                  sx={{ fontSize: 16, mr: 0.5, verticalAlign: "middle" }}
                />
                Bathrooms
              </Typography>
              <ToggleButtonGroup
                value={filters.bathrooms || ""}
                exclusive
                onChange={(e, value) =>
                  value !== null && updateFilter("bathrooms", value)
                }
                size="small"
                fullWidth
              >
                <ToggleButton value="">Any</ToggleButton>
                <ToggleButton value="1">1</ToggleButton>
                <ToggleButton value="2">2</ToggleButton>
                <ToggleButton value="3">3+</ToggleButton>
              </ToggleButtonGroup>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Clear Filters */}
      {activeCount > 0 && (
        <Button
          variant="outlined"
          fullWidth
          startIcon={<ClearIcon sx={{ fontSize: '18px' }} />}
          onClick={clearFilters}
          sx={{
            mt: 2,
            py: 1,
            borderRadius: 2,
            textTransform: "none",
            fontWeight: 500,
            fontSize: "0.875rem",
            color: "text.secondary",
            borderColor: "divider",
            "&:hover": {
              borderColor: "error.main",
              color: "error.main",
              bgcolor: "rgba(211, 47, 47, 0.04)",
            },
          }}
        >
          Clear All Filters ({activeCount})
        </Button>
      )}
    </Box>
  );

  // If showHeader is false, only return FilterContent (used in modals)
  if (!showHeader) {
    return <FilterContent />;
  }

  // For desktop/full version with header
  return (
    <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 3,
        }}
      >
        <Typography
          variant="h6"
          sx={{ display: "flex", alignItems: "center", gap: 1 }}
        >
          <FilterIcon />
          Filters
        </Typography>
        {activeCount > 0 && (
          <Chip label={activeCount} size="small" color="primary" />
        )}
      </Box>
      <Divider sx={{ mb: 3 }} />
      <FilterContent />
    </Paper>
  );
}
